import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity, CheckCircle2, Circle, Calendar, Timer, Plus, Trash2, MessageSquare,
  Play, Pause, RotateCcw, MapPin, Clock, User as UserIcon, ArrowLeft, Trophy,
  Star, Repeat, Sparkles, ChevronDown, ChevronUp, ShieldCheck, Lock, Maximize2, Minimize2,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import { IconAvatar } from "@/components/app/IconAvatar";
import OverlapCalendar from "@/components/app/OverlapCalendar";
import StreakIcon from "@/components/app/StreakIcon";
import WorkspaceSwitcher from "@/components/app/WorkspaceSwitcher";
import {
  createWorkspaceSession, createWorkspaceTask, deleteWorkspaceSession, deleteWorkspaceTask,
  getWorkspace, getWorkspaceFocusPresence, getSoloFocus, getTasks, saveWorkspaceCheckin,
  startWorkspaceFocusSession, updateWorkspaceFocusSession,
  startSoloFocusSession, updateSoloFocusSession,
  toggleWorkspaceTask, verifyWorkspaceTask, submitExplorationDecision, proposeWorkspaceSprint,
  acceptWorkspaceSprint, settleWorkspaceSprint,
  allocateWorkspaceSprintStake,
  nudgeWorkspaceTask,
} from "@/lib/api";
import { TaskRow, TaskForm, todayISO, scheduledToday } from "@/components/app/TaskUI";
import {
  PhaseBanner, DecisionPanel, SprintSetupPanel, SprintStakesCard, EndedPanel, SprintSettlementPanel,
} from "@/components/app/LifecyclePanels";
import { getProfile, getUser } from "@/lib/storage";
import { rememberWorkspace } from "@/lib/workspaces";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const tab = params.get("tab") || "checkin";
  const setTab = (t) => setParams({ tab: t });

  useEffect(() => {
    setMe(getProfile());
    let cancelled = false;
    const load = async () => {
      try {
        const workspace = await getWorkspace(id);
        if (!cancelled) { setP(workspace); rememberWorkspace(id); }
      } catch (error) {
        toast("Could not load workspace.", { description: error.message });
        if (!cancelled) navigate("/matches");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const refresh = async () => setP(await getWorkspace(id));
  const partner = p?.partner?.profile;
  if (loading) return <AppShell><div className="px-6 sm:px-10 py-10 text-sm text-[#A1A1AA]">Loading workspace...</div></AppShell>;
  if (!p || !partner) return null;
  // Between two workspaces the previous partner's page stays up, dimmed, until the next one arrives.
  const switching = p.id !== id;

  const dayNumberInPhase = () => p.lifecycle?.dayNumber || 1;

  const today = new Date().toISOString().slice(0, 10);
  const todaysCheckin = p.checkins.find((c) => c.date === today);
  const streak = p.stats.streakDays;
  // Today's scheduled tasks (recurring on today's DoW OR open one-off) — for header pill.
  const jsDay = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date().getDay()];
  const myTasksToday = p.tasks.filter((t) => (t.owner === "me" || t.owner === "shared") && scheduledToday(t));
  const doneMyToday = myTasksToday.filter((t) => (t.completions || []).includes(today));
  const daysTogether = p.stats.daysTogether;
  const fullWorkspace = Boolean(p.capabilities?.fullWorkspace);
  const mixedTierCheckinOnly = Boolean(p.capabilities?.mixedTierCheckinOnly);
  const stakesEnabled = Boolean(p.capabilities?.partnershipStakes);
  const trialAccess = Boolean(p.capabilities?.fullWorkspaceFromTrial);
  // A cohort with "unlock all features" opens the whole workspace whatever the member's plan.
  const cohortAccess = p.capabilities?.fullWorkspaceFromCohort || null;

  const tabs = [
    { key: "checkin", label: "Daily check-in", icon: StreakIcon },
    { key: "calendar", label: "Shared calendar", icon: Calendar },
    { key: "sessions", label: "Sessions", icon: Calendar, locked: !fullWorkspace },
    { key: "focus", label: "Focus room", icon: Timer, locked: !fullWorkspace },
    { key: "tasks", label: "Tasks", icon: CheckCircle2, locked: !fullWorkspace },
  ];

  return (
    <AppShell>
      <div className="px-6 sm:px-10 py-10 max-w-6xl mx-auto">
        <button onClick={() => navigate("/dashboard")} className="text-xs text-[#A1A1AA] hover:text-[#F5F5F0] inline-flex items-center gap-2 mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        <WorkspaceSwitcher currentId={id} tab={tab} />

        {/* Keyed by partnership so no form or timer state crosses from one partner's workspace to another's. */}
        <div key={p.id} aria-busy={switching} className={`transition-opacity duration-200 ${switching ? "pointer-events-none opacity-40" : ""}`}>

        {/* Header card */}
        <div className="hm-card p-6 sm:p-8 rounded-md mb-6" data-testid="ws-header">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="flex -space-x-3">
                <IconAvatar iconKey={me?.profileIcon || "Rocket"} size="lg" tone="outline" className="ring-2 ring-[#0A0A0A]" />
                <IconAvatar iconKey={partner.profileIcon} size="lg" className="ring-2 ring-[#0A0A0A]" />
              </div>
              <div>
                <div className="overline mb-2">{(() => {
                  const ph = p.lifecycle.phase;
                   if (ph === "ended") return "Partnership · ended";
                   if (ph === "pending") return "Partnership · awaiting confirmation";
                   if (ph === "active") return `Partnership · Day ${p.stats.daysTogether}`;
                  if (ph === "sprint") return `Sprint ${p.sprint?.number || 1} · Day ${dayNumberInPhase(p)} of ${p.sprint?.lengthDays || 21}`;
                  if (ph === "sprint_setup") return `Sprint Pact · setup`;
                   if (ph === "decision") return `Starter Sprint · decision time`;
                   return `Starter Sprint · Day ${dayNumberInPhase(p)} of 7`;
                })()}</div>
                <h1 className="font-serif-editorial font-light tracking-tighter text-3xl sm:text-4xl leading-tight">
                  You <span className="text-[#F59E0B]">&</span> {partner.fullName.split(" ")[0]}
                </h1>
                <div className="text-xs text-[#A1A1AA] flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {partner.city}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {partner.timezone}</span>
                  <span>· {partner.commitmentDuration} commitment</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatPill icon={StreakIcon} iconProps={{ lit: streak > 0 }} label="Streak" value={`${streak}d`} testId="ws-streak" />
              {fullWorkspace ? <StatPill icon={Trophy} label="Focus" value={`${Math.round(p.focus.totalMinutes / 60)}h`} /> : <StatPill icon={Lock} label="Workspace" value="Pro" />}
              {trialAccess && <StatPill icon={Sparkles} label="Full trial" value={`${p.capabilities.trialDaysLeft}d`} />}
              {cohortAccess && <StatPill icon={Sparkles} label={cohortAccess.name} value="All unlocked" testId="ws-cohort-access" />}
              {fullWorkspace && <StatPill icon={CheckCircle2} label="Today" value={`${doneMyToday.length}/${myTasksToday.length}`} testId="ws-tasks-stat" />}
              <Link to={`/chat/${p.matchId}`} className="hm-btn-ghost inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs">
                <MessageSquare className="w-3.5 h-3.5" /> Chat
              </Link>
              <Link to={`/profile/${p.partner.userId}`} className="hm-btn-ghost inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs">
                <UserIcon className="w-3.5 h-3.5" /> Profile
              </Link>
            </div>
          </div>
        </div>

        {/* Tab strip */}
        <div className="flex items-center gap-1 border-b border-white/5 mb-6 overflow-x-auto" data-testid="ws-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`ws-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-[#F59E0B] text-[#F5F5F0]"
                  : "border-transparent text-[#A1A1AA] hover:text-[#F5F5F0]"
              }`}
            >
              {t.locked ? <Lock className="w-3.5 h-3.5" strokeWidth={1.5} /> : <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {t.label}
            </button>
          ))}
        </div>

        {/* Lifecycle: phase banner + decision + sprint setup + ended */}
        {(() => {
          const lifecycle = p.lifecycle;
          const phase = lifecycle.phase;
           const lifecyclePartnership = { ...p, ...lifecycle, explorationDecision: lifecycle.explorationDecision, sprintHistory: lifecycle.sprintHistory, endReason: lifecycle.endReason };
           const partnerFirstName = partner.fullName.split(" ")[0];
           if (phase === "ended") return <EndedPanel partnership={lifecyclePartnership} partnerName={partnerFirstName} />;
           if (phase === "pending") return <PendingPartnershipPanel partnerName={partnerFirstName} />;
           if (phase === "exploration") return <PhaseBanner phase={phase} dayNumber={lifecycle.dayNumber} daysLeft={lifecycle.daysLeft} partnerName={partnerFirstName} />;
           if (phase === "decision") {
             return (
               <DecisionPanel
                 partnership={lifecyclePartnership} partnerName={partnerFirstName} stakesEnabled={stakesEnabled}
                 onDecide={async (decision, msg) => {
                   try {
                     await submitExplorationDecision(p.id, { decision, message: msg || null });
                     toast(decision === "no" ? "Partnership ended." : "Decision saved.", {
                       description: decision === "no" ? "The match credit remains used." : `Waiting for ${partnerFirstName}'s decision.`,
                     });
                     await refresh();
                   } catch (error) {
                     toast("Could not save your decision.", { description: error.message });
                   }
                 }}
               />
             );
           }
           if (!stakesEnabled) return null;
           if (!p.capabilities?.sprints) {
             return <PhaseBanner phase={phase} dayNumber={lifecycle.dayNumber} daysLeft={lifecycle.daysLeft} partnerName={partnerFirstName} />;
          }
          if (phase === "sprint_end") {
            if (lifecycle.sprint) {
              return (
                <SprintSettlementPanel
                  partnership={{ ...lifecyclePartnership, sprint: lifecycle.sprint, __liveSettlement: lifecycle.settlement }} partnerName={partnerFirstName}
                  onNewSprint={async () => {
                    try { await settleWorkspaceSprint(p.id, lifecycle.sprint.id); await refresh(); }
                    catch (error) { toast("Could not settle sprint.", { description: error.message }); }
                  }}
                />
              );
            }
          }
          if (phase === "sprint_setup") {
            return (
              <>
              {lifecycle.sprint?.status === "pending" && !lifecycle.sprint.myStakeAllocated && <div className="hm-card p-5 rounded-md mb-6 border border-[#F59E0B]/30"><div className="overline mb-2">Stake allocation</div><p className="text-sm text-[#A1A1AA] mb-4">Allocate {lifecycle.sprint.stakeTokens} credits from your wallet before this Sprint Pact can begin.</p><div className="flex gap-3"><button onClick={async () => { try { await allocateWorkspaceSprintStake(p.id, lifecycle.sprint.id); toast("Stake allocated."); await refresh(); } catch (error) { toast("Could not allocate stake.", { description: error.message }); } }} className="hm-btn-primary px-4 py-2 rounded-md text-sm">Allocate {lifecycle.sprint.stakeTokens} credits</button><Link to="/wallet" className="hm-btn-ghost px-4 py-2 rounded-md text-sm">Get credits</Link></div></div>}
              <SprintSetupPanel
                partnership={lifecyclePartnership} partnerName={partnerFirstName}
                onStart={async ({ lengthDays, stakeTokens }) => {
                  try {
                    if (lifecycle.sprint?.status === "pending") {
                      if (!lifecycle.sprint.myStakeAllocated) {
                        await allocateWorkspaceSprintStake(p.id, lifecycle.sprint.id);
                      }
                      if (lifecycle.sprint.proposerUserId === me?.userId) {
                        toast("Your credits are locked.", { description: "Waiting for your partner to accept." });
                      } else {
                        await acceptWorkspaceSprint(p.id, lifecycle.sprint.id);
                      }
                    } else {
                      await proposeWorkspaceSprint(p.id, { lengthDays, stakeTokens });
                    }
                    await refresh();
                  } catch (error) { toast("Could not update the Sprint Pact.", { description: error.message }); }
                }}
              />
              </>
            );
          }
          return (
            <PhaseBanner
              phase={phase}
              dayNumber={lifecycle.dayNumber}
              daysLeft={lifecycle.daysLeft}
              partnerName={partnerFirstName}
              sprint={lifecycle.sprint}
            />
          );
        })()}

        {/* Live stakes card during sprint */}
        {(() => {
          const phase = p.lifecycle.phase;
          if (phase !== "sprint") return null;
          const settlement = p.lifecycle.settlement;
          return (
            <div className="mb-6">
              <SprintStakesCard settlement={settlement} sprint={p.lifecycle.sprint} partnerName={partner.fullName.split(" ")[0]} />
            </div>
          );
        })()}

        {tab === "checkin" && <CheckinTab p={p} todaysCheckin={todaysCheckin} refresh={refresh} partner={partner} />}
        {tab !== "checkin" && tab !== "calendar" && !fullWorkspace && <LockedWorkspacePanel section={tabs.find((item) => item.key === tab)?.label || "This section"} mixedTier={mixedTierCheckinOnly} />}
        {tab === "calendar" && <OverlapCalendar p={p} me={me} refresh={refresh} />}
        {tab === "sessions" && fullWorkspace && <SessionsTab p={p} partner={partner} refresh={refresh} />}
        {tab === "focus" && fullWorkspace && <FocusTab p={p} partner={partner} refresh={refresh} />}
        {tab === "tasks" && fullWorkspace && <TasksTab p={p} partner={partner} refresh={refresh} stakesEnabled={stakesEnabled} />}
        </div>
      </div>
    </AppShell>
  );
}

function LockedWorkspacePanel({ section, mixedTier = false }) {
  return (
    <div className="hm-card p-10 rounded-md text-center border border-[#F59E0B]/20" data-testid="workspace-pro-lock">
      <Lock className="w-7 h-7 text-[#F59E0B] mx-auto mb-3" />
      <h2 className="font-serif-editorial text-2xl mb-2">{section} is included with Pro.</h2>
      <p className="text-sm text-[#A1A1AA] max-w-lg mx-auto">{mixedTier ? "In a Free–paid partnership, your Free workspace includes daily check-ins only. Upgrade for tasks, sessions, focus rooms, heatmaps, and shared tools." : "Your full-workspace trial has ended. Free partnerships keep daily check-ins; upgrade for tasks, sessions, focus rooms, heatmaps, and shared workspace tools."}</p>
    </div>
  );
}

function PendingPartnershipPanel({ partnerName }) {
  return (
    <div className="hm-card p-8 rounded-md mb-6 border border-white/10">
      <div className="overline mb-3">Waiting for mutual confirmation</div>
      <h2 className="font-serif-editorial text-2xl mb-3">Your Starter Sprint begins when {partnerName} joins.</h2>
      <p className="text-sm text-[#A1A1AA]">Use chat to align on expectations. The seven-day clock and shared workspace access begin only after both of you confirm the partnership.</p>
    </div>
  );
}

function StatPill({ icon: Icon, iconProps, label, value, testId }) {
  return (
    <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 bg-white/[0.02]" data-testid={testId}>
      <Icon className="w-3.5 h-3.5 text-[#F59E0B]" strokeWidth={1.5} {...iconProps} />
      <div>
        <div className="font-serif-editorial text-lg leading-none">{value}</div>
        <div className="text-[9px] text-[#A1A1AA] uppercase tracking-[0.18em]">{label}</div>
      </div>
    </div>
  );
}

/* ---------- Check-in tab ---------- */
function CheckinTab({ p, todaysCheckin, refresh, partner }) {
  const [form, setForm] = useState(() => todaysCheckin || { mood: 3, hours: 3, wins: "", blockers: "", tomorrow: "" });
  const [view, setView] = useState("mine"); // 'mine' | 'partner'
  useEffect(() => { setForm(todaysCheckin || { mood: 3, hours: 3, wins: "", blockers: "", tomorrow: "" }); }, [todaysCheckin]);

  const submit = async () => {
    try {
      await saveWorkspaceCheckin(p.id, form);
      await refresh();
      toast(todaysCheckin ? "Check-in updated." : "Check-in logged.", { description: "Momentum acknowledged." });
    } catch (error) { toast("Could not save check-in.", { description: error.message }); }
  };

  const last7 = useMemo(() => {
    const arr = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      arr.push({ iso, c: p.checkins.find((x) => x.date === iso) });
    }
    return arr;
  }, [p.checkins]);

  const partnerLast7 = useMemo(() => {
    const arr = [];
    const list = p.partnerCheckins || [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      arr.push({ iso, c: list.find((x) => x.date === iso) });
    }
    return arr;
  }, [p.partnerCheckins]);

  const moodLabel = (m) => ["Rough", "Slow", "OK", "Solid", "On fire"][(m || 3) - 1];

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div className="hm-card p-6 rounded-md" data-testid="ws-checkin">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-editorial text-2xl">Today, {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</h2>
          {todaysCheckin && <span className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B]">Editing today's log</span>}
        </div>

        <div className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">How's the day feeling?</div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((m) => {
                const active = form.mood === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setForm({ ...form, mood: m })}
                    data-testid={`ws-mood-${m}`}
                    className={`flex-1 px-3 py-3 rounded-md border text-xs uppercase tracking-wider ${
                      active ? "border-[#F59E0B] bg-[#F59E0B] text-black font-semibold" : "border-white/10 text-[#A1A1AA] hover:text-[#F5F5F0]"
                    }`}
                  >
                    {moodLabel(m)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">
              <span>Focused hours today</span>
              <span className="font-serif-editorial text-[#F59E0B] text-lg normal-case">{form.hours}h</span>
            </div>
            <input
              type="range" min={0} max={16} step={0.5} value={form.hours}
              onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })}
              data-testid="ws-hours"
              className="w-full accent-[#F59E0B]"
            />
          </div>

          <CkField label="Wins" placeholder="Shipped landing v2. Cracked 3 GATE mocks. Ran 5k." value={form.wins} onChange={(v) => setForm({ ...form, wins: v })} testId="ws-wins" />
          <CkField label="Blockers" placeholder="Got stuck on auth. Skipped morning study." value={form.blockers} onChange={(v) => setForm({ ...form, blockers: v })} testId="ws-blockers" />
          <CkField label="Tomorrow's #1 focus" placeholder="One clear, unambiguous priority for tomorrow." value={form.tomorrow} onChange={(v) => setForm({ ...form, tomorrow: v })} testId="ws-tomorrow" />

          <button onClick={submit} data-testid="ws-checkin-submit" className="hm-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold">
            {todaysCheckin ? "Update check-in" : "Log check-in"}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Timeline switcher */}
        <div className="hm-card p-5 rounded-md">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Last 7 days</div>
            <div className="flex items-center gap-1 border border-white/10 rounded-md p-1 text-[10px]">
              <button onClick={() => setView("mine")} data-testid="ws-ck-view-mine" className={`px-2 py-0.5 rounded-sm ${view === "mine" ? "bg-[#F59E0B] text-black" : "text-[#A1A1AA]"}`}>You</button>
              <button onClick={() => setView("partner")} data-testid="ws-ck-view-partner" className={`px-2 py-0.5 rounded-sm ${view === "partner" ? "bg-[#F59E0B] text-black" : "text-[#A1A1AA]"}`}>{partner.fullName.split(" ")[0]}</button>
            </div>
          </div>
          <ul className="space-y-2">
            {(view === "mine" ? last7 : partnerLast7).map(({ iso, c }) => (
              <li key={iso} className="flex items-start gap-3 text-xs">
                <span className={`mt-1 w-2 h-2 rounded-full ${c ? "bg-[#F59E0B]" : "bg-white/10"}`} />
                <span className="text-[#A1A1AA] font-light w-14 shrink-0">{new Date(iso).toLocaleDateString(undefined, { weekday: "short" })}</span>
                <span className="text-[#F5F5F0] font-light flex-1">
                  {c ? `${c.hours}h · ${moodLabel(c.mood)}` : <span className="italic text-[#7a7a80]">skipped</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Latest partner entry — full detail */}
        {view === "partner" && (
          <div className="hm-card p-5 rounded-md" data-testid="ws-ck-partner-entry">
            {(() => {
              const latest = [...(p.partnerCheckins || [])].sort((a, b) => b.date.localeCompare(a.date))[0];
              if (!latest) return <div className="text-xs text-[#A1A1AA] italic">{partner.fullName.split(" ")[0]} hasn't checked in yet.</div>;
              return (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <IconAvatar iconKey={partner.profileIcon} size="sm" tone="outline" />
                    <div>
                      <div className="text-sm">{partner.fullName.split(" ")[0]}'s latest check-in</div>
                      <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em]">{new Date(latest.date).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <Line k="Hours" v={`${latest.hours}h`} />
                    <Line k="Mood" v={moodLabel(latest.mood)} />
                    {latest.wins && <Line k="Wins" v={latest.wins} />}
                    {latest.blockers && <Line k="Blockers" v={latest.blockers} />}
                    {latest.tomorrow && <Line k="Tomorrow" v={latest.tomorrow} />}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        <div className="hm-card p-5 rounded-md">
          <div className="overline mb-3">Why this matters</div>
          <p className="text-xs text-[#A1A1AA] font-light leading-relaxed">
            Public check-ins turn a private goal into a social contract. Your partner sees this. Skipping is silent — the silence is loud.
          </p>
        </div>
      </div>
    </div>
  );
}
function Line({ k, v }) {
  return <div className="grid grid-cols-[80px_1fr] gap-3"><span className="text-[#A1A1AA] font-light text-xs uppercase tracking-[0.18em]">{k}</span><span className="text-[#F5F5F0] font-light">{v}</span></div>;
}
function CkField({ label, placeholder, value, onChange, testId }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">{label}</div>
      <textarea
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="hm-input w-full px-3 py-2.5 rounded-md text-sm font-light placeholder:text-[#5b5b60] resize-none"
        data-testid={testId}
      />
    </div>
  );
}

/* ---------- Sessions tab ---------- */
function SessionsTab({ p, partner, refresh }) {
  const [form, setForm] = useState({ day: "Mon", start: "07:00", end: "08:00", title: "", type: "Silent co-work" });
  const add = async () => {
    if (!form.title.trim()) { toast("Session needs a title."); return; }
    try {
      await createWorkspaceSession(p.id, form);
      setForm({ ...form, title: "" });
      await refresh();
    } catch (error) { toast("Could not schedule session.", { description: error.message }); }
  };
  const del = async (sid) => {
    try { await deleteWorkspaceSession(p.id, sid); await refresh(); }
    catch (error) { toast("Could not remove session.", { description: error.message }); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="hm-card p-6 rounded-md" data-testid="ws-sessions">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-editorial text-2xl">Recurring co-work sessions</h2>
          <span className="text-xs text-[#A1A1AA]">{p.sessions.length} scheduled</span>
        </div>

        <div className="grid grid-cols-[80px_90px_90px_1fr_140px_auto] gap-2 mb-5 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Day</div>
            <select value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} className="hm-input w-full px-2 py-2 rounded-md text-sm">
              {DAYS.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Start</div>
            <input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="hm-input w-full px-2 py-2 rounded-md text-sm" data-testid="ws-sess-start" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">End</div>
            <input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="hm-input w-full px-2 py-2 rounded-md text-sm" data-testid="ws-sess-end" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Title</div>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Morning DSA · Novel writing…" className="hm-input w-full px-3 py-2 rounded-md text-sm" data-testid="ws-sess-title" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Type</div>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="hm-input w-full px-2 py-2 rounded-md text-sm" data-testid="ws-sess-type">
              {["Silent co-work", "Voice", "Video", "Screen share"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <button onClick={add} data-testid="ws-sess-add" className="hm-btn-primary px-3 py-2 rounded-md text-xs font-semibold inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>

        {p.sessions.length === 0 ? (
          <div className="text-sm text-[#A1A1AA] italic py-6">No sessions yet. Schedule your first co-working slot above.</div>
        ) : (
          <div className="space-y-2">
            {DAYS.map((d) => {
              const list = p.sessions.filter((s) => s.day === d);
              if (list.length === 0) return null;
              return (
                <div key={d}>
                  <div className="overline mb-2">{d}</div>
                  <div className="space-y-2 mb-4">
                    {list.sort((a, b) => a.start.localeCompare(b.start)).map((s) => (
                      <div key={s.id} className="flex items-center gap-3 p-3 rounded-md border border-white/10 bg-[#0f0f0f]">
                        <span className="font-serif-editorial text-[#F59E0B] w-24">{s.start} → {s.end}</span>
                        <span className="text-sm flex-1">{s.title}</span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] px-2 py-0.5 rounded-sm border border-white/10">{s.type}</span>
                        <button onClick={() => del(s.id)} className="text-[#7a7a80] hover:text-[#F5F5F0]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="hm-card p-5 rounded-md">
        <div className="overline mb-3">Partner's schedule</div>
        <div className="text-sm text-[#F5F5F0] mb-2">{partner.availableStart || "Availability not specified"} {partner.availableEnd ? `- ${partner.availableEnd}` : ""}</div>
        <div className="text-xs text-[#A1A1AA] mb-3">Working days: {(partner.workingDays || []).join(" · ") || "Not specified"}</div>
        <div className="text-xs text-[#A1A1AA]">Prefers: {(partner.communication || []).slice(0, 3).join(", ") || "Not specified"}</div>
      </div>
    </div>
  );
}

/* ---------- Focus room tab ---------- */
export function FocusTab({ p, partner, refresh, solo = false, initialTaskId = "" }) {
  const [duration, setDuration] = useState(25);
  const [remaining, setRemaining] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const [session, setSession] = useState(null);
  const [partnerPresence, setPartnerPresence] = useState({ online: false, status: null });
  const [customMinutes, setCustomMinutes] = useState(30);
  const [extraMinutes, setExtraMinutes] = useState(10);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [personalTasks, setPersonalTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(initialTaskId);
  const [firstAction, setFirstAction] = useState("");
  const roomRef = useRef(null);
  const intervalRef = useRef(null);
  const completingRef = useRef(false);
  const winterArcUser = getUser()?.subscriptionTier === "winter_arc";
  const sessionId = session?.id;
  const sessionStatus = session?.status;

  useEffect(() => {
    let active = true;
    getTasks().then((response) => {
      if (active) setPersonalTasks(response.tasks || []);
    }).catch(() => {
      if (active) toast("Could not load tasks for the focus launchpad.");
    });
    return () => { active = false; };
  }, []);

  useEffect(() => { if (!session) setSelectedTaskId(initialTaskId); }, [initialTaskId, session]);

  useEffect(() => {
    if (!session) setRemaining(duration * 60);
  }, [duration, session]);

  useEffect(() => {
    let active = true;
    const syncPresence = async () => {
      try {
        const presence = solo ? await getSoloFocus() : await getWorkspaceFocusPresence(p.id);
        if (!active) return;
        if (!solo) setPartnerPresence(presence.partner || { online: false, status: null });
        const openSession = solo ? presence.session : presence.me?.session;
        if (!sessionId && openSession) {
          const restored = openSession;
          setSession(restored);
          setDuration(restored.plannedDurationMinutes);
          setRemaining(Math.max(0, restored.plannedDurationMinutes * 60 - restored.focusedSeconds));
          setRunning(restored.status === "active");
          setSelectedTaskId(restored.taskId || "");
          setFirstAction(restored.firstAction || "");
        }
      } catch {
        if (active) setPartnerPresence({ online: false, status: null });
      }
    };
    syncPresence();
    const timer = window.setInterval(syncPresence, 10000);
    return () => { active = false; window.clearInterval(timer); };
  }, [p?.id, sessionId, solo]);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setRemaining((value) => Math.max(0, value - 1));
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  useEffect(() => {
    if (!sessionId || !["active", "paused"].includes(sessionStatus)) return undefined;
    const heartbeat = async () => {
      try {
        const updated = solo
          ? await updateSoloFocusSession(sessionId, "heartbeat")
          : await updateWorkspaceFocusSession(p.id, sessionId, "heartbeat");
        setSession(updated);
      } catch { /* the next presence poll will reconcile */ }
    };
    const timer = window.setInterval(heartbeat, 15000);
    return () => window.clearInterval(timer);
  }, [p?.id, sessionId, sessionStatus, solo]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === roomRef.current);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const complete = async () => {
    if (!session || completingRef.current) return;
    completingRef.current = true;
    setRunning(false);
    try {
      const completed = solo
        ? await updateSoloFocusSession(session.id, "complete")
        : await updateWorkspaceFocusSession(p.id, session.id, "complete");
      toast(`Focus session complete — ${completed.durationMinutes} min logged.`, { description: <span>{completed.taskId ? "Mark the task complete separately when the work is finished. " : "The full session timeline is saved. "}{completed.focusedSeconds >= 60 && <Link to="/flex" className="underline">Create your Flex stamp →</Link>}</span> });
      setSession(null);
      setRemaining(duration * 60);
      try { await refresh(); } catch { /* The session is complete even if refreshing stats fails. */ }
      try { new Audio("data:audio/wav;base64,UklGRh4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=").play(); } catch { /* noop */ }
    } catch (error) {
      toast("Could not complete focus session.", { description: error.message });
    } finally {
      completingRef.current = false;
    }
  };

  useEffect(() => {
    if (running && session && remaining === 0) complete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, running, session?.id]);

  const start = async () => {
    try {
      const created = solo
        ? await startSoloFocusSession(duration, selectedTaskId || null, firstAction.trim() || null)
        : await startWorkspaceFocusSession(p.id, duration, selectedTaskId || null, firstAction.trim() || null);
      setSession(created);
      setDuration(created.plannedDurationMinutes);
      setRemaining(Math.max(0, created.plannedDurationMinutes * 60 - created.focusedSeconds));
      setRunning(created.status === "active");
      setSelectedTaskId(created.taskId || "");
      setFirstAction(created.firstAction || "");
    } catch (error) { toast("Could not start focus session.", { description: error.message }); }
  };

  const pause = async () => {
    try {
      const updated = solo ? await updateSoloFocusSession(session.id, "pause") : await updateWorkspaceFocusSession(p.id, session.id, "pause");
      setSession(updated);
      setRunning(false);
    } catch (error) { toast("Could not pause focus session.", { description: error.message }); }
  };

  const resume = async () => {
    try {
      const updated = solo ? await updateSoloFocusSession(session.id, "resume") : await updateWorkspaceFocusSession(p.id, session.id, "resume");
      setSession(updated);
      setRunning(true);
    } catch (error) { toast("Could not resume focus session.", { description: error.message }); }
  };

  const addTime = async (minutes) => {
    const addition = Number(minutes);
    if (!Number.isInteger(addition) || addition < 1 || addition > 480) {
      toast("Enter between 1 and 480 minutes.");
      return;
    }
    try {
      const updated = solo ? await updateSoloFocusSession(session.id, "add_time", addition) : await updateWorkspaceFocusSession(p.id, session.id, "add_time", addition);
      setSession(updated);
      setDuration(updated.plannedDurationMinutes);
      setRemaining((value) => value + addition * 60);
      toast(`${addition} minutes added.`);
    } catch (error) { toast("Could not add time.", { description: error.message }); }
  };

  const reset = async () => {
    setRunning(false);
    if (session) {
      try { await (solo ? updateSoloFocusSession(session.id, "cancel") : updateWorkspaceFocusSession(p.id, session.id, "cancel")); }
      catch (error) { toast("Could not close focus session.", { description: error.message }); return; }
    }
    setSession(null);
    setRemaining(duration * 60);
  };

  const applyCustomDuration = () => {
    const minutes = Number(customMinutes);
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 480) {
      toast("Choose a starting duration between 1 and 480 minutes.");
      return;
    }
    setDuration(minutes);
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await roomRef.current?.requestFullscreen();
    } catch { toast("Fullscreen is not available in this browser."); }
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const pct = Math.max(0, Math.min(100, ((duration * 60 - remaining) / (duration * 60)) * 100));
  const partnerInRoom = Boolean(partnerPresence.online);
  const activeTasks = personalTasks.filter((task) => !task.done && !task.doneToday);
  const selectedTask = activeTasks.find((task) => task.id === selectedTaskId);

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div ref={roomRef} className="focus-room-card hm-card p-6 sm:p-8 rounded-md text-center relative overflow-auto" data-testid="ws-focus">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F59E0B]/[0.03] to-transparent pointer-events-none" />
        <button onClick={toggleFullscreen} className="absolute right-4 top-4 z-10 rounded-md border border-white/10 p-2 text-[#A1A1AA] transition hover:border-white/30 hover:text-white" aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
        <div className="focus-room-overline overline mb-3">{solo ? "Focus room · solo" : "Focus room · silent co-work"}</div>
        <h2 className="focus-room-title font-serif-editorial text-3xl mb-8">{solo ? "One task. One focused block." : "Deep work, together in silence."}</h2>

        {!session ? (
          <div className="focus-room-launchpad mx-auto mb-6 max-w-lg rounded-md border border-white/10 bg-black/30 p-4 text-left">
            <div className="overline mb-3">Focus launchpad</div>
            <label className="block text-xs text-[#A1A1AA]">
              Work on one task
              <select value={selectedTaskId} onChange={(event) => setSelectedTaskId(event.target.value)} className="hm-input mt-1 w-full rounded-md px-3 py-2 text-sm text-[#F5F5F0]">
                <option value="">No task attached</option>
                {activeTasks.map((task) => <option key={task.id} value={task.id}>{task.text}</option>)}
              </select>
            </label>
            <label className="mt-3 block text-xs text-[#A1A1AA]">
              First concrete action <span className="text-[#777]">· optional</span>
              <input type="text" maxLength={160} value={firstAction} onChange={(event) => setFirstAction(event.target.value)} placeholder="e.g. Draft the opening paragraph" className="hm-input mt-1 w-full rounded-md px-3 py-2 text-sm" />
            </label>
            {activeTasks.length === 0 && <p className="mt-3 text-xs text-[#A1A1AA]">No unfinished tasks yet. <Link to="/tasks" className="text-[#F59E0B] underline">Add one</Link>, or start without attaching a task.</p>}
          </div>
        ) : (
          <div className="mx-auto mb-6 max-w-lg rounded-md border border-white/10 bg-black/30 px-4 py-3 text-left">
            <div className="overline mb-1">Current focus</div>
            <div className="text-sm text-[#F5F5F0]">{session.taskText || selectedTask?.text || "Open focus block"}</div>
            {session.firstAction && <div className="mt-1 text-xs text-[#A1A1AA]">Start with: {session.firstAction}</div>}
          </div>
        )}

        {winterArcUser ? (
          <div className="focus-room-clock-wrap mb-7">
            <SplitFlapTimer minutes={mm} seconds={ss} />
            <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-white/50">{running ? "Discipline in progress" : session ? "Paused · hold the line" : "Ready for the arc"}</div>
          </div>
        ) : (
          <div className="focus-room-dial relative w-56 h-56 mx-auto mb-6">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
              <circle cx="50" cy="50" r="46" fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray={`${(2 * Math.PI * 46).toFixed(2)}`} strokeDashoffset={`${(2 * Math.PI * 46 * (1 - pct / 100)).toFixed(2)}`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="focus-room-time font-serif-editorial text-5xl tracking-tighter" data-testid="ws-focus-time">{mm}:{ss}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mt-1">{running ? "In session" : session ? "Paused" : "Ready"}</div>
            </div>
          </div>
        )}

        <div className="focus-room-presets flex flex-wrap justify-center gap-2 mb-3">
          {[25, 45, 60, 90].map((m) => (
            <button
              key={m}
              onClick={() => { if (!session) setDuration(m); }}
              data-testid={`ws-focus-${m}`}
              className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                duration === m ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F5F5F0]" : "border-white/10 text-[#A1A1AA] hover:text-[#F5F5F0]"
              } ${session ? "opacity-40 pointer-events-none" : ""}`}
            >
              {m} min
            </button>
          ))}
        </div>

        {!session && <div className="focus-room-custom mx-auto mb-6 flex max-w-xs items-end justify-center gap-2">
          <label className="text-left"><span className="text-[9px] uppercase tracking-[0.16em] text-[#A1A1AA]">Custom minutes</span><input type="number" min="1" max="480" value={customMinutes} onChange={(event) => setCustomMinutes(event.target.value)} className="hm-input mt-1 w-28 rounded-md px-3 py-2 text-sm" /></label>
          <button onClick={applyCustomDuration} className="hm-btn-ghost rounded-md px-3 py-2 text-xs">Set</button>
        </div>}

        {session && <div className="focus-room-extension mx-auto mb-6 max-w-md rounded-md border border-white/10 bg-black/30 p-3">
          <div className="mb-2 text-[9px] uppercase tracking-[0.18em] text-[#A1A1AA]">Add focus time</div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[5, 10, 25].map((minutes) => <button key={minutes} onClick={() => addTime(minutes)} className="hm-btn-ghost rounded-md px-3 py-2 text-xs">+{minutes} min</button>)}
            <input type="number" min="1" max="480" value={extraMinutes} onChange={(event) => setExtraMinutes(event.target.value)} aria-label="Custom extra minutes" className="hm-input w-20 rounded-md px-2 py-2 text-xs" />
            <button onClick={() => addTime(extraMinutes)} className="hm-btn-ghost rounded-md px-3 py-2 text-xs">Add</button>
          </div>
        </div>}

        <div className="focus-room-actions flex justify-center gap-3">
          {!session ? (
            <button onClick={start} data-testid="ws-focus-start" className="hm-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold">
              <Play className="w-4 h-4" fill="currentColor" /> Start
            </button>
          ) : !running ? (
            <button onClick={resume} data-testid="ws-focus-start" className="hm-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold">
              <Play className="w-4 h-4" fill="currentColor" /> Resume
            </button>
          ) : (
            <button onClick={pause} data-testid="ws-focus-pause" className="hm-btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold">
              <Pause className="w-4 h-4" /> Pause
            </button>
          )}
          <button onClick={reset} data-testid="ws-focus-reset" className="hm-btn-ghost inline-flex items-center gap-2 px-4 py-3 rounded-md text-sm" aria-label="Reset focus session" title="Reset and save as cancelled">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {!solo && <div className="hm-card p-5 rounded-md">
          <div className="overline mb-3">Presence</div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <IconAvatar iconKey={partner.profileIcon} size="md" tone="outline" />
            </div>
            <div>
              <div className="text-sm">{partner.fullName.split(" ")[0]}</div>
              <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em]">{partnerInRoom ? (partnerPresence.status === "paused" ? "Timer paused" : "Focusing now") : "Focus history shared"}</div>
            </div>
          </div>
          <p className="text-xs text-[#A1A1AA] mt-4 font-light">
            {partnerInRoom
              ? "They're focused. Match the energy."
              : "Not in a live focus session right now. Your own session is still saved."}
          </p>
        </div>}
        <div className="hm-card p-5 rounded-md">
          <div className="overline mb-3">Focus stats</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-md border border-white/5">
              <div className="font-serif-editorial text-2xl">{p.focus.sessions}</div>
              <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em] mt-1">Sessions</div>
            </div>
            <div className="p-3 rounded-md border border-white/5">
              <div className="font-serif-editorial text-2xl">{Math.round(p.focus.totalMinutes / 60 * 10) / 10}h</div>
              <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em] mt-1">Total</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SplitFlapTimer({ minutes, seconds }) {
  const value = `${minutes}:${seconds}`;
  return (
    <div className="split-flap-clock" role="timer" aria-label={`${minutes} minutes and ${seconds} seconds remaining`} data-testid="ws-focus-time">
      {value.split("").map((character, index) => character === ":"
        ? <span key={`separator-${index}`} className="split-flap-separator">:</span>
        : <SplitFlapDigit key={`digit-${index}`} character={character} />)}
    </div>
  );
}

function SplitFlapDigit({ character }) {
  const previousCharacter = useRef(character);
  const [transition, setTransition] = useState(null);

  useLayoutEffect(() => {
    const from = previousCharacter.current;
    if (from === character) return undefined;
    previousCharacter.current = character;
    setTransition({ from, to: character });
    const timeout = window.setTimeout(() => setTransition(null), 440);
    return () => window.clearTimeout(timeout);
  }, [character]);

  return (
    <span className="split-flap-digit" aria-hidden="true">
      <span className="split-flap-half split-flap-top"><span>{character}</span></span>
      <span className="split-flap-half split-flap-bottom"><span>{transition?.from ?? character}</span></span>
      {transition && (
        <>
          <span className="split-flap-leaf split-flap-leaf-top"><span>{transition.from}</span></span>
          <span className="split-flap-leaf split-flap-leaf-bottom"><span>{transition.to}</span></span>
        </>
      )}
    </span>
  );
}

/* ---------- Tasks tab ---------- */
function TasksTab({ p, partner, refresh, stakesEnabled }) {
  const [view, setView] = useState("today"); // 'today' | 'mine' | 'partner'
  const [showAdd, setShowAdd] = useState(false);

  const myTasks = p.tasks || [];
  const partnerTasks = p.partnerTasks || [];

  const tog = async (workspaceTaskId) => {
    try { await toggleWorkspaceTask(p.id, workspaceTaskId); await refresh(); }
    catch (error) { toast("Could not update task.", { description: error.message }); }
  };
  const del = async (workspaceTaskId) => {
    try { await deleteWorkspaceTask(p.id, workspaceTaskId); await refresh(); }
    catch (error) { toast("Could not delete task.", { description: error.message }); }
  };
  const verifyPartnerTask = async (partnerTaskId, completionDate) => {
    try { await verifyWorkspaceTask(p.id, partnerTaskId, completionDate); await refresh(); }
    catch (error) { toast("Could not verify task.", { description: error.message }); }
  };
  const nudgePartnerTask = async (partnerTaskId) => {
    try {
      await nudgeWorkspaceTask(p.id, partnerTaskId);
      toast("Nudge sent.", { description: "One gentle reminder — no notification spam." });
      await refresh();
    } catch (error) { toast("Could not send nudge.", { description: error.message }); }
  };
  const submit = async (form) => {
    try {
      await createWorkspaceTask(p.id, form);
      toast("Task added.");
      setShowAdd(false);
      await refresh();
    } catch (error) { toast("Could not add task.", { description: error.message }); }
  };

  const todaysMine = myTasks.filter(scheduledToday);
  const todaysPartner = partnerTasks.filter((task) => scheduledToday(task) || hasUnverifiedCompletion(task));
  const doneTodayCount = (list) => list.filter((t) => (t.completions || []).includes(todayISO())).length;

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6" data-testid="ws-tasks">
      <div className="hm-card p-6 rounded-md">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-serif-editorial text-2xl">Task board</h2>
          <div className="flex items-center gap-1 border border-white/10 rounded-md p-1 text-xs">
            {[
              { k: "today", label: "Today" },
              { k: "mine", label: "Your tasks" },
              { k: "partner", label: `${partner.fullName.split(" ")[0]}'s tasks` },
            ].map((v) => (
              <button
                key={v.k} onClick={() => setView(v.k)}
                data-testid={`ws-tasks-view-${v.k}`}
                className={`px-3 py-1 rounded-sm ${view === v.k ? "bg-[#F59E0B] text-black" : "text-[#A1A1AA]"}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {view !== "partner" && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button
              onClick={() => setShowAdd((s) => !s)}
              data-testid="ws-task-toggle-add"
              className="hm-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs"
            >
              {showAdd ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showAdd ? "Close" : "New recurring task"}
            </button>
            <Link to="/tasks" data-testid="ws-tasks-open-page" className="hm-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs">
              <Sparkles className="w-4 h-4" /> Import from your task page →
            </Link>
          </div>
        )}
        {showAdd && view !== "partner" && (
          <TaskForm onSubmit={submit} onCancel={() => setShowAdd(false)} stakesEnabled={stakesEnabled} />
        )}

        {view === "today" && (
          <div className="space-y-6">
            <div className="text-xs text-[#A1A1AA]">
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <SectionList
              title="Your tasks today"
              testId="ws-tasks-today-mine"
              tasks={todaysMine}
              mode="self"
              onToggle={tog} onDelete={del}
              stakesEnabled={stakesEnabled}
              emptyText="Nothing scheduled today. Add a recurring task above or take an intentional rest day."
            />
            <SectionList
              title={`${partner.fullName.split(" ")[0]}'s tasks today`}
              testId="ws-tasks-today-partner"
              tasks={todaysPartner}
              mode="partner"
              onVerify={verifyPartnerTask}
              onNudge={nudgePartnerTask}
              stakesEnabled={stakesEnabled}
              emptyText={`${partner.fullName.split(" ")[0]} hasn't scheduled anything for today.`}
            />
          </div>
        )}
        {view === "mine" && (
          <TaskListSorted tasks={myTasks} emptyText="No tasks yet. Add your first recurring commitment or import from your task page." mode="self" onToggle={tog} onDelete={del} stakesEnabled={stakesEnabled} />
        )}
        {view === "partner" && (
          <TaskListSorted tasks={partnerTasks} emptyText="Your partner hasn't set tasks yet." mode="partner" onVerify={verifyPartnerTask} onNudge={nudgePartnerTask} stakesEnabled={stakesEnabled} />
        )}
      </div>

      <div className="space-y-4">
        <div className="hm-card p-5 rounded-md">
          <div className="overline mb-3">Today's tally</div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <MiniStatCard label="You" value={`${doneTodayCount(todaysMine)}/${todaysMine.length}`} tone="amber" />
            <MiniStatCard label={partner.fullName.split(" ")[0]} value={`${doneTodayCount(todaysPartner)}/${todaysPartner.length}`} tone="outline" />
          </div>
          <p className="text-xs text-[#A1A1AA] font-light">
            Recurring tasks reset every scheduled day. Whether you did it yesterday doesn't matter — <span className="text-[#F5F5F0]">today matters.</span>
          </p>
        </div>
        <div className="hm-card p-5 rounded-md">
          <div className="overline mb-3">Verification</div>
          <p className="text-xs text-[#A1A1AA] font-light leading-relaxed">
            When your partner marks something done, tap <span className="text-[#F5F5F0]">Verify</span> next to their task. It's on your honour — but partnerships that verify each other's work land 3× more consistency.
          </p>
        </div>
      </div>
    </div>
  );
}

function hasUnverifiedCompletion(task) {
  const verified = new Set(task.verifications || []);
  return (task.completions || []).some((date) => !verified.has(date));
}

function SectionList({ title, tasks, mode, onToggle, onDelete, onVerify, onNudge, emptyText, testId, stakesEnabled }) {
  return (
    <div data-testid={testId}>
      <div className="overline mb-3">{title} <span className="text-[#7a7a80] ml-1">· {tasks.length}</span></div>
      {tasks.length === 0 ? (
        <div className="text-sm text-[#A1A1AA] italic py-3">{emptyText}</div>
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} mode={mode} onToggle={onToggle} onDelete={onDelete} onVerify={onVerify} onNudge={onNudge} stakesEnabled={stakesEnabled} />
          ))}
        </ul>
      )}
    </div>
  );
}
function TaskListSorted({ tasks, emptyText, mode, onToggle, onDelete, onVerify, onNudge, stakesEnabled }) {
  if (tasks.length === 0) return <div className="text-sm text-[#A1A1AA] italic py-6">{emptyText}</div>;
  const sorted = [...tasks].sort((a, b) => {
    const aT = scheduledToday(a) ? 1 : 0; const bT = scheduledToday(b) ? 1 : 0;
    if (aT !== bT) return bT - aT;
    return (b.importance || 3) - (a.importance || 3);
  });
  return (
    <ul className="space-y-2">
      {sorted.map((t) => (
        <TaskRow key={t.id} task={t} mode={mode} onToggle={onToggle} onDelete={onDelete} onVerify={onVerify} onNudge={onNudge} stakesEnabled={stakesEnabled} />
      ))}
    </ul>
  );
}
function MiniStatCard({ label, value, tone = "amber" }) {
  return (
    <div className={`p-3 rounded-md ${tone === "amber" ? "border border-[#F59E0B]/30 bg-[#F59E0B]/5" : "border border-white/10 bg-white/[0.02]"}`}>
      <div className="font-serif-editorial text-2xl text-[#F5F5F0]">{value}</div>
      <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em] mt-1">{label}</div>
    </div>
  );
}

function computeStreak(checkins) {
  if (checkins.length === 0) return 0;
  const set = new Set(checkins.map((c) => c.date));
  let streak = 0;
  const cur = new Date();
  const today = cur.toISOString().slice(0, 10);
  if (!set.has(today)) cur.setDate(cur.getDate() - 1);
  while (true) {
    const iso = cur.toISOString().slice(0, 10);
    if (set.has(iso)) { streak++; cur.setDate(cur.getDate() - 1); } else break;
  }
  return streak;
}
